import { UIIcon } from '../config/icons';
import { abMakeMediaCard } from '../lib/ab/media-card';
import { ABCitySelect, ABProducerSelect, ABSceneSelect, ABVenueSelect, ABVideoSelect } from '../lib/ab/select-admin';
import { ABInputArray, ABInputSuggestions, ABMultiCurrencyInput, ABNewPicturesArray } from '../lib/ab/widgets-admin';
import { cBookingsByEvent } from '../lib/mdl/c-booking-admin';
import { c_cities } from '../lib/mdl/c-city';
import { cCountryDefaultCurrency } from '../lib/mdl/c-country';
import { CEventFlag, CSessionFlag } from '../lib/mdl/c-event';
import { CEvent, cEventActiveSessions, CEventAdminView, cEventAdminViewGet, cEventCreate, cEventDelete, cEventDuplicate, cEventGetSession, cEventUpdate, CSession, cSessionCancel, cSessionDefaultInstructions, cSessionFullTime, cSessionSalesEnded, cSessionsCreate, cSessionsUpdate, cSessionUpdate, CVideo, cVideoDelete, cVideosCreate, cVideoUpdate } from '../lib/mdl/c-event-admin';
import { c_event_keywords, cEventKeywordsRegister } from '../lib/mdl/c-event-keyword';
import { c_event_tags, CEventTagId, cEventTagName } from '../lib/mdl/c-event-tag';
import { c_producers, CEventDefaults, CProducerEmail, CProducerEmailKind, cProducerGetAutofill, CProducerId, CTicketType } from '../lib/mdl/c-producer';
import { c_venues } from '../lib/mdl/c-venue';
import { EMinimumGuarantee, eMinimumGuaranteeCreate, eMinimumGuaranteesByEvent, EPayoutType } from '../lib/mdl/e-payout';
import { i_events, IEvent, ISession } from '../lib/mdl/i-provider';
import { SUploadResponse } from '../lib/mdl/s-upload';
import { UMediaLink, uMediaLinkParse } from '../lib/mdl/u-media-link';
import { uInputTranslator } from '../lib/mdl/u-translate';
import { v_videos } from '../lib/mdl/v-video';
import { UIDataAdapter, UIInputAdapter } from '../lib/ui/component/adapter';
import { UIButton } from '../lib/ui/component/button';
import { UIChip, UIChipInput, UIChips } from '../lib/ui/component/chip';
import { UIDateInput, UIDateTimeInput, UIWeekdaysSelect } from '../lib/ui/component/date';
import { UIDialog } from '../lib/ui/component/dialog';
import { UII18NInput } from '../lib/ui/component/i18n-input';
import { UIImageInput } from '../lib/ui/component/image';
import { UIFixedCurrencyInput, UINumberInput, UITextInput } from '../lib/ui/component/input';
import { UILabeled } from '../lib/ui/component/labeled';
import { UIList } from '../lib/ui/component/list';
import { UIMarkdownInput } from '../lib/ui/component/markdown';
import { UIMultiSelect, UISelect } from '../lib/ui/component/select';
import { UITabs } from '../lib/ui/component/tabs';
import { uiToast } from '../lib/ui/component/toast';
import { UICheckbox, UICheckboxRadioOptions, UIExclusiveToggles, UIMultiToggles, UIRadio, UISwitch, UITickCheckbox } from '../lib/ui/component/toggle';
import { addTooltip } from '../lib/ui/component/tooltip';
import { showWarning } from '../lib/ui/component/warning';
import { $el } from '../lib/ui/elements';
import { UIScreen } from '../lib/ui/screen';
import { UIRelPos, UIValue } from '../lib/ui/types';
import { UIView, UIViewSpec } from '../lib/ui/view';
import { YPaymentAmount } from '../lib/y/amount';
import { $log, xavis_experiments } from '../lib/y/debug';
import { $on } from '../lib/y/events';
import { yTextToBlob } from '../lib/y/file-utils';
import { $form } from '../lib/y/form';
import { $format } from '../lib/y/format';
import { $http } from '../lib/y/http';
import { $i18n, yGetI18N, YI18NString, yIsEmptyI18N } from '../lib/y/i18n';
import { $location } from '../lib/y/location';
import { $date, $time, YDate, yIntervalTime, YTime } from '../lib/y/time';
import { YFlags, YFlagsUpdate, YId } from '../lib/y/types';

declare module '../lib/ui/augment-var' {
    export interface UITriggerDefinitions {
        'ui-view-reappeared': void;
    }
}

const tz = 'Europe/Stockholm';

interface RepeatSession {
    is_drop_in: boolean;
    time: YTime;
    end_time?: YTime;
    sales_end: number;
    duration: number;
    quantity: number;
    ticket_type: CTicketType;
    instructions: YI18NString;
    base_price: YPaymentAmount;
    payout_price?: YPaymentAmount;
    flags: YFlags<CSessionFlag>;
}

export class AdminRepeatSessionScreen extends UIScreen {
    constructor(private _event_id: YId, private _session_id: YId) {
        super();
        this.title = 'Repeat session';
    }

    protected override async fillContent(content: UIViewSpec) {
        const event = await cEventAdminViewGet(this._event_id);
        const session = cEventGetSession(event, this._session_id);

        content.classList.add('tl-admin-page');

        const period_values = [{ key: 'd', name: 'Daily' }, { key: 'w', name: 'Weekly' }, { key: 'm', name: 'Monthly' }];
        const period = new UISelect(period_values, { required: true });
        period.value = 'd';

        const weekdays = new UIWeekdaysSelect();
        const l_weekdays = new UILabeled(weekdays, { above: 'Week days' });

        const from = new UIDateInput({ required: true });
        const until = new UIDateInput({ required: true });

        const session_time = $time(session.time);
        const session_date = session_time.date;

        from.value = session_date;
        until.value = session_date;

        const form = $form({ _: [period, from, until] });

        let sessions: RepeatSession[];

        const create = new UIButton({
            title: 'Update', classes: 'm-round', on_click: () => {
                if(!form.validate()) return;
                return cSessionsCreate(event, sessions as any).then($location.pop);
            }
        });

        const session_list = $el.DIV('tl-repeat-session-list');

        const recalc = () => {
            const v_period = period.value;
            const scale = v_period == 'd' ? $time.day : v_period == 'w' ? $time.week : $time.month;
            const dates: YDate[] = [];
            const v_weekdays = weekdays.value;

            for(let date = from.value, end = until.value; date.n <= end.n; date = date.add(1, scale)) {
                if(v_period == 'd' && !v_weekdays.includes(date.day_of_week)) continue;
                dates.push(date);
            }

            let end_diff: number | undefined;
            if(session.is_drop_in)
                end_diff = $time(session.end_time!).n - session_time.n;

            sessions = [];

            for(const d of dates) {
                if(d.n == session_date.n) continue;

                const start = d.time(session_time.hours, session_time.minutes);

                const new_session: RepeatSession = { is_drop_in: session.is_drop_in, time: start, sales_end: session.sales_end, duration: session.duration, quantity: session.quantity, ticket_type: session.ticket_type, instructions: session.instructions, base_price: session.base_price, payout_price: session.payout_price, flags: session.flags };
                if(end_diff != null)
                    new_session.end_time = $time(start.n + end_diff);

                sessions.push(new_session);
            }

            session_list.ui_empty();

            if(sessions.length) {
                session_list.ui_add([$el.DIV('r-title', sessions.length + ' sessions will be added')]);
                if(sessions.length > 20) {
                    for(let i = 0; i < 8; i++) {
                        session_list.ui_add($el.DIV('r-time', yIntervalTime(sessions[i].time, sessions[i].end_time, tz)));
                    }
                    session_list.ui_add([$el.DIV('r-time', '⋮')]);
                    for(let i = sessions.length - 8; i < sessions.length; i++) {
                        session_list.ui_add($el.DIV('r-time', yIntervalTime(sessions[i].time, sessions[i].end_time, tz)));
                    }
                } else {
                    for(const s of sessions) {
                        session_list.ui_add($el.DIV('r-time', yIntervalTime(s.time, s.end_time, tz)));
                    }
                }
            } else {
                session_list.ui_add($el.DIV('r-title', 'No sessions to add'));
            }
        }

        $on(period, 'value', v => {
            if(v == 'd')
                l_weekdays.ui_show();
            else
                l_weekdays.ui_hide();

            recalc();
        });

        $on(from, 'value', recalc);
        $on(until, 'value', recalc);
        $on(weekdays, 'value', recalc);
        recalc();

        content.ui_add($el.FORM('ui-section w-form l-form', [
            $el.H3('heading', 'Original session: ' + cSessionFullTime(session)),
            $el.DIV('l-input-row', [
                new UILabeled(period, { above: 'Repetition period' }),
                l_weekdays
            ]),
            $el.DIV('l-input-row', [
                new UILabeled(from, { above: 'From' }),
                new UILabeled(until, { above: 'Until' })
            ]),
            session_list,
            $el.DIV('l-button-row', [create])
        ]));
    }
}

class ImportSessionsDialog extends UIDialog {
    constructor(event: CEventAdminView, sessions: ISession[], last_session?: CSession, defaults?: CEventDefaults) {
        super({ close: true, classes: 'm-500' });

        const amount = new UINumberInput({ required: true });

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('subtitle', yGetI18N(event.name)),
            sessions.map(s => $el.B($time(s.time).format('yyyy-MM-dd HH:mm'))),
            $el.DIV('s-small'),
            new UILabeled(amount, { above: 'Tickets per session' }),
            $el.DIV('l-button-row', [
                new UIButton({ title: "Cancel", classes: 'm-round m-compact', on_click: () => { this.dismiss(null) } }),
                new UIButton({
                    title: "Import", classes: 'm-round m-compact m-hollow', on_click: () => {
                        const value = amount.value || 0;
                        const new_sessions = sessions.map(s => ({
                            time: s.time,
                            quantity: value,
                            imported_id: s.id,
                            base_price: s.base_price ?? last_session?.base_price ?? defaults?.base_price,
                            duration: s.duration ?? last_session?.duration ?? defaults?.duration,
                            sales_end: last_session?.sales_end ?? defaults?.sales_end ?? 5,
                            ticket_type: last_session?.ticket_type ?? defaults?.ticket_type ?? 'email',
                            instructions: last_session?.instructions ?? defaults?.instructions ?? {},
                            flags: last_session?.flags ?? defaults?.session_flags ?? {}
                        }));

                        return cSessionsCreate(event, new_sessions).then(() => { this.dismiss(true) }, uiToast);
                    }
                })
            ])
        ]));
    }
}

export class AdminSessionScreen extends UIScreen {
    constructor(private _event_id: YId, private _session_id?: YId) {
        super();
        this.title = 'Session';
    }

    protected override async fillContent(content: UIViewSpec) {
        content.classList.add('tl-admin-page');

        const event = await cEventAdminViewGet(this._event_id);
        const session = this._session_id && cEventGetSession(event, this._session_id);

        $log('app', 'session', this._session_id, session);

        const is_managed = session?.managed_by_id != null;

        const currency = cCountryDefaultCurrency(c_cities.maybe(event.city_id)!.country);

        const is_drop_in = new UICheckbox({ title: 'Drop-in session' });
        const time = new UIDateTimeInput({ required: true });
        const end_time = new UIDateTimeInput();
        const quantity = new UINumberInput({ required: true, disabled: is_managed });
        if(is_managed)
            addTooltip("Can't change amount for managed event", quantity, { offset: 8, position: UIRelPos.Left })

        const sales_end = new UINumberInput({ decimal: true });
        const duration = new UINumberInput();
        const base_price = new UIFixedCurrencyInput({ default_currency: currency });
        const payout_price = new UIFixedCurrencyInput({ default_currency: currency });
        const dont_buy_more = new UICheckbox({ title: 'Disable "buy more" for this session' });
        const ticket_type_values: UIValue<CTicketType>[] = [{ key: 'pickup', name: 'Pickup' }, { key: 'email', name: 'E-ticket' }];
        const ticket_type = new UISelect(ticket_type_values, { required: true });
        const instructions = new UII18NInput(new UITextInput({ lines: 3 }), { translator: uInputTranslator });
        const set_default_instructions = new UIButton({
            kind: 'k-text', classes: 'm-inline ui-input-helper', title: 'Insert standard text', on_click: () => {
                instructions.value = cSessionDefaultInstructions(ticket_type.value);
            }
        });
        const restriction_values: UIValue<CSessionFlag>[] = [
            { key: 'loyalty', name: 'Loyalty session' },
            { key: 'no_booking_limit', name: 'Ignore for 2-booking limit' },
            { key: 'no_same_day_limit', name: 'Allow bookings same day' },
            { key: 'no_same_event_limit', name: 'Allow going more than once' },
        ];
        const restrictions = new UIMultiSelect(restriction_values, {});

        const form = $form({ _: [dont_buy_more, restrictions], is_drop_in, time, end_time, quantity, duration, base_price, payout_price, sales_end, instructions, ticket_type });

        const section = $el.FORM('ui-section w-form l-form', [
            session && $el.B('Session ID: ' + session.id),
            is_drop_in,
            new UILabeled(time, { above: 'Session time' }),
            new UILabeled(end_time, { above: 'End time' }),
            new UILabeled(sales_end, { above: 'Sales end (hours before event)' }),
            new UILabeled(duration, { above: 'Duration (minutes)' }),
            new UILabeled(quantity, { above: 'Quantity' }),
            new UILabeled(base_price, { above: 'Regular price' }),
            new UILabeled(payout_price, { above: 'Payout amount' }),
            dont_buy_more,
            new UILabeled(ticket_type, { above: 'Ticket type' }),
            new UILabeled(instructions, { above: 'Booking instructions' }),
            set_default_instructions,
            new UILabeled(restrictions, { above: 'Booking restrictions' })
        ]);

        // TODO: fix it so that the ui_visible_proxy works if it is set after something has been hidden

        end_time.ui_hide();


        $on(is_drop_in, 'value', d => {
            if(d) {
                end_time.required = true;
                end_time.ui_show();
            } else {
                end_time.required = false;
                end_time.ui_hide();
            }
        });

        const button = new UIButton({ icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button' });

        button.on_click = () => {
            if(!form.validate()) return;
            if(is_drop_in.value && !end_time.value.after(time.value)) {
                // TODO: add validation to the inputs themselves
                end_time.showError("End time must be after start time");
                return;
            }

            const value = form.value;

            const flags: YFlags<CSessionFlag> = {};
            for(const r of restrictions.value) {
                flags[r] = true;
            }
            if(dont_buy_more.value)
                flags['no_buy_more'] = true;

            value.flags = flags;

            if(session) {
                const session_time = $time(session.time);
                if(!time.value!.equal(session_time) && session.quantity_reserved) {
                    return showWarning({ texts: ["Det är inbokade medlemmar till den här event-sessionen. Är du säker på att du vill ändra speltid enligt följande?", 'Tidigare tid: ' + session_time.format('yyyy-MM-dd HH:mm'), 'Ny tid: ' + time.value!.format('yyyy-MM-dd HH:mm')] }).then(() => {
                        return cSessionUpdate(event, session.id, value).then($location.pop, uiToast);
                    });
                } else {
                    return cSessionUpdate(event, session.id, value).then($location.pop, uiToast);
                }
            } else {
                return cSessionsCreate(event, [value]).then($location.pop);
            }
        };

        if(session) {
            $log('app', 'opening session', session.id);

            form.value = session;

            dont_buy_more.value = session.flags['no_buy_more'];
            restrictions.value = Object.keys(session.flags);
        } else {
            if(event.sessions.length) {
                const last = event.sessions[event.sessions.length - 1];
                is_drop_in.value = last.is_drop_in;
                sales_end.value = last.sales_end;
                base_price.value = last.base_price;
                dont_buy_more.value = last.flags['no_buy_more'];
                payout_price.value = last.payout_price;
                ticket_type.value = last.ticket_type;
                duration.value = last.duration;
                restrictions.value = Object.keys(last.flags);
                if(!yIsEmptyI18N(last.instructions))
                    instructions.value = last.instructions;
                else if(last.ticket_type)
                    instructions.value = cSessionDefaultInstructions(last.ticket_type);
            } else {
                const defaults = c_producers.maybe(event.producer_id)?.event_defaults
                if(defaults) {
                    if(defaults.is_drop_in)
                        is_drop_in.value = defaults.is_drop_in;
                    if(defaults.sales_end)
                        sales_end.value = defaults.sales_end;
                    if(defaults.session_flags?.['no_buy_more'])
                        dont_buy_more.value = true;
                    if(defaults.base_price)
                        base_price.value = defaults.base_price;
                    if(defaults.payout_price)
                        payout_price.value = defaults.payout_price;
                    if(defaults.duration)
                        duration.value = defaults.duration;
                    if(defaults.ticket_type)
                        ticket_type.value = defaults.ticket_type;
                    if(!yIsEmptyI18N(defaults.instructions))
                        instructions.value = defaults.instructions;
                    else if(defaults.ticket_type)
                        instructions.value = cSessionDefaultInstructions(defaults.ticket_type);
                }
            }

            if(!ticket_type.value)
                ticket_type.value = 'pickup'
        }

        addTooltip(session ? 'Update' : 'Create', button, { offset: 8, hover: button, position: UIRelPos.Above });

        content.ui_add([section, button]);
    }
}

class UpdateSessionsScreen extends UIScreen {
    constructor(private _event: CEventAdminView, private _sessions: CSession[]) {
        super();
        this.title = 'Update sessions';
    }

    protected override fillContent(content: UIViewSpec) {
        content.classList.add('tl-admin-page');

        const event = this._event;
        const sessions = this._sessions;

        const currency = cCountryDefaultCurrency(c_cities.maybe(event.city_id)!.country);

        const sales_end = new UINumberInput({ decimal: true, disabled: true });
        const duration = new UINumberInput({ required: true, disabled: true });
        const quantity = new UINumberInput({ required: true, disabled: true });
        const base_price = new UIFixedCurrencyInput({ default_currency: currency, disabled: true });
        const payout_price = new UIFixedCurrencyInput({ default_currency: currency, disabled: true });
        const dont_buy_more = new UICheckbox({ title: 'Disable "buy more" for this session', disabled: true });
        const ticket_type_values: UIValue<CTicketType>[] = [{ key: 'pickup', name: 'Pickup' }, { key: 'email', name: 'E-ticket' }];
        const ticket_type = new UISelect(ticket_type_values, { required: true, disabled: true });
        const instructions = new UII18NInput(new UITextInput({ lines: 5, disabled: true }), { translator: uInputTranslator });
        const set_default_instructions = new UIButton({
            kind: 'k-text', title: 'Insert standard text', on_click: () => {
                instructions.value = cSessionDefaultInstructions(ticket_type.value || 'email');
            }
        });
        set_default_instructions.disabled = true;
        const restriction_values: UIValue<CSessionFlag>[] = [
            { key: 'loyalty', name: 'Loyalty session' },
            { key: 'no_booking_limit', name: 'Ignore for 2-booking limit' },
            { key: 'no_same_day_limit', name: 'Allow bookings same day' },
            { key: 'no_same_event_limit', name: 'Allow going more than once' },
        ];
        const restrictions = new UIMultiSelect(restriction_values, { disabled: true });

        const check_sales_end = new UICheckbox;
        const check_duration = new UICheckbox;
        const check_quantity = new UICheckbox;
        const check_base_price = new UICheckbox;
        const check_payout_price = new UICheckbox;
        const check_dont_buy_more = new UICheckbox;
        const check_ticket_type = new UICheckbox;
        const check_instructions = new UICheckbox;
        const check_restrictions = new UICheckbox;

        const section = $el.FORM('ui-section w-form l-form', [
            $el.DIV('l-input-row', [check_sales_end, new UILabeled(sales_end, { above: 'Sales end (hours before event)' })]),
            $el.DIV('l-input-row', [check_duration, new UILabeled(duration, { above: 'Duration (minutes)' })]),
            $el.DIV('l-input-row', [check_quantity, new UILabeled(quantity, { above: 'Quantity' })]),
            $el.DIV('l-input-row', [check_base_price, new UILabeled(base_price, { above: 'Regular price' })]),
            $el.DIV('l-input-row', [check_payout_price, new UILabeled(payout_price, { above: 'Payout price' })]),
            $el.DIV('l-input-row', [check_dont_buy_more, dont_buy_more]),
            $el.DIV('l-input-row', [check_ticket_type, new UILabeled(ticket_type, { above: 'Ticket type' })]),
            $el.DIV('l-input-row', [check_instructions, $el.DIV('i-horizontal', [new UILabeled(instructions, { above: 'Booking instructions' }), set_default_instructions])]),
            $el.DIV('l-input-row', [check_restrictions, new UILabeled(restrictions, { above: 'Booking restrictions' })])
        ]);

        sales_end.value = sessions[0].sales_end;
        duration.value = sessions[0].duration;
        quantity.value = sessions[0].quantity;
        base_price.value = sessions[0].base_price;
        payout_price.value = sessions[0].payout_price;
        dont_buy_more.value = sessions[0].flags['no_buy_more'];
        ticket_type.value = sessions[0].ticket_type;
        restrictions.value = Object.keys(sessions[0].flags);
        instructions.value = sessions[0].instructions;

        $on(check_sales_end, 'value', v => { sales_end.disabled = !v; });
        $on(check_duration, 'value', v => { duration.disabled = !v; });
        $on(check_quantity, 'value', v => { quantity.disabled = !v; });
        $on(check_base_price, 'value', v => { base_price.disabled = !v; });
        $on(check_payout_price, 'value', v => { payout_price.disabled = !v; });
        $on(check_dont_buy_more, 'value', v => { dont_buy_more.disabled = !v; });
        $on(check_ticket_type, 'value', v => { ticket_type.disabled = !v; });
        $on(check_instructions, 'value', v => { instructions.disabled = !v; set_default_instructions.disabled = !v; });
        $on(check_restrictions, 'value', v => { restrictions.disabled = !v; });

        const button = new UIButton({
            icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => {
                const update: Partial<CSession> = {};
                if(check_sales_end.value) update.sales_end = sales_end.value;
                if(check_duration.value) update.duration = duration.value;
                if(check_quantity.value) update.quantity = quantity.value;
                if(check_base_price.value) update.base_price = base_price.value;
                if(check_payout_price.value) update.payout_price = payout_price.value;
                if(check_ticket_type.value) update.ticket_type = ticket_type.value;
                if(check_instructions.value) update.instructions = instructions.value;

                const flags: YFlagsUpdate<CSessionFlag> = {}
                if(check_dont_buy_more.value) flags.no_buy_more = dont_buy_more.value;
                if(check_restrictions.value) {
                    const v = restrictions.value;
                    flags.loyalty = v.includes('loyalty');
                    flags.no_booking_limit = v.includes('no_booking_limit');
                    flags.no_same_day_limit = v.includes('no_same_day_limit');
                    flags.no_same_event_limit = v.includes('no_same_event_limit');
                }

                if(Object.keys(flags).length)
                    update.flags = flags as YFlags<CSessionFlag>;

                if(!Object.keys(update).length) {
                    uiToast('No properties selected');
                    return;
                }

                return cSessionsUpdate(event, sessions.map(s => s.id), update).then($location.pop, uiToast);
            }
        });

        addTooltip('Update all', button, { offset: 8, hover: button, position: UIRelPos.Above });

        content.ui_add([section, button]);
    }
}

class VideoScreen extends UIScreen {
    constructor(private _event: CEvent, private _video: CVideo, private _callback: () => void) {
        super();
        this.title = 'Video';
    }

    protected override fillContent(content: UIViewSpec): void {
        const event = this._event;
        const video = this._video;
        content.classList.add('tl-admin-page');

        const vod = new ABVideoSelect({ required: true });
        // const date = new UIDateTimeInput({ required: true });
        const expires = new UIDateTimeInput({ placeholder: 'Expiration date (optional)' });
        const is_ppv = new UICheckbox({ title: 'PPV' });
        const is_open = new UICheckbox({ title: 'Open to everyone with the link' });
        const is_member_open = new UICheckbox({ title: 'Open to inactive members' });
        const base_price = new ABMultiCurrencyInput(100);
        const payout_amount = new UIFixedCurrencyInput({ default_currency: 'SEK' });

        const country_values = [{ key: 'SE', title: 'Sweden' }, { key: 'DE', title: 'Germany' }];
        const availability = new UIMultiToggles(country_values, v => new UICheckbox(v), { required: true, classes: 'l-form' });

        const form = $form({ vod, expires, is_ppv, is_open, is_member_open, availability, base_price, payout_amount });

        const section = $el.FORM('ui-section w-form l-form');


        section.ui_add([
            new UILabeled(vod, { above: 'Video' }),
        ]);
        // } else {
        //     form.add({ stream: stream, date: date });

        //     section.ui_add([
        //         new UILabeled(stream, { above: 'Stream' }),
        //         new UILabeled(date, { above: 'Stream date' }),
        //     ]);

        //     if(xavis_experiments) {
        //         const vod = new TLVODSelect();
        //         form.add({ vod: vod });
        //         section.ui_add(new UILabeled(vod, { above: 'VOD (optional)'}))
        //     }
        // }

        section.ui_add([
            new UILabeled(expires, { above: 'Expiration date (optional)' }),
            is_ppv,
            is_open,
            is_member_open,
            new UILabeled(base_price, { above: 'Regular price' }),
            new UILabeled(payout_amount, { above: 'Payout amount' }),
            new UILabeled(availability, { above: 'Availability' })
        ]);

        const button = new UIButton({
            icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => {
                if(!form.validate()) return;
                return cVideoUpdate(event.id, video.id, form.value).then(() => {
                    this._callback?.();
                    $location.pop();
                }, uiToast);
            }
        });

        form.value = video;

        addTooltip('Update', button, { offset: 8, hover: button, position: UIRelPos.Above });

        content.ui_add([section, button]);
    }
}

const punctuation_regex = /(\s|[!-#%-\*,-\/:;\?@\[-\]_\{\}\u2000-\u206F\u2E00-\u2E7F])+/g

function recommendKeywords(text: string, current: string[]) {
    text = ' ' + text.toLocaleLowerCase().replace(punctuation_regex, ' ') + ' ';
    return c_event_keywords.filter(k => !current.includes(k) && text.includes(' ' + k + ' '));
}

class EventInfoTab extends UIView {
    constructor(event: CEventAdminView | undefined, duplicate: CEventAdminView | undefined, remove_callback: () => void, add_callback: ((event: CEventAdminView) => void) | undefined, imported?: IEvent) {
        super('div', 'tl-admin-page m-sidebar');

        const sidebar = $el.DIV('tl-sidebar')

        const is_published = new UISwitch;
        const name = new UII18NInput(new UITextInput, { translator: uInputTranslator, required: true });
        const slug = new UITextInput({ required: true, null_if_empty: true });
        const city_id = new ABCitySelect({ required: true });
        const venue_id = new ABVenueSelect({ required: true });
        const producer_id = new ABProducerSelect({ required: true });
        const coproducer_ids = new ABInputArray('+ Add co-producer', () => new ABProducerSelect({ required: true }), undefined);
        $on(city_id, 'value', city => { if(city) venue_id.city = city });
        const scene_id = new ABSceneSelect({ required: true });
        $on(venue_id, 'value', venue => { scene_id.venue = venue });
        const show_scene = new UICheckbox({ title: 'Show scene in events' });
        const all_event_tags = c_event_tags.all.filter(t => !t.dynamic).map(t => ({ key: t.id, name: cEventTagName(t) })).sort((a, b) => a.name.localeCompare(b.name));
        const event_tag_select = new UISelect(all_event_tags, { placeholder: 'Add tag', searchable: true, compact_title: '+ Add another tag' });
        event_tag_select.compact_mode = true;
        const event_tag_ids = new UIChips<CEventTagId>({ to_chip: c => cEventTagName(c_event_tags.maybe(c)!), classes: 'm-collapse-empty' });

        const keywords = new UIChipInput({
            to_chip: s => s.trim().toLocaleLowerCase(),
            autocomplete: text => Promise.resolve(c_event_keywords.filter(k => k.includes(text)).map(k => ({ key: k, name: k }))),
            clear_button: true
        });

        const description = new UII18NInput(new UIMarkdownInput({ lines: 8, resizeable: true }), { translator: uInputTranslator })

        const addMediaLink = () => {
            uMediaLinkParse(media_link.value).then(link => {
                media_links.push(link);
                media_link.value = '';
            }, uiToast);
        }

        const media_link = new UITextInput({ placeholder: $i18n.pr.event_add_media_link });
        const media_link_add = new UIButton({ title: $i18n.pr.event_add_link, classes: 'm-round m-compact ui-tint-subtle', on_click: addMediaLink });
        media_link.on_enter = addMediaLink;

        const media_links = new UIInputAdapter((l: UMediaLink, entry) => {
            return {
                view: abMakeMediaCard(l, () => { media_links.remove(entry) }),
                input: $form.noop()
            };
        });

        const boost = new UINumberInput();
        const vat = new UINumberInput();
        const invoice_name = new UITextInput({ null_if_empty: true });
        const dont_market = new UICheckbox({ title: 'Don\'t market outside Abundo' });
        const dont_feature = new UICheckbox({ title: 'Don\'t show as featured' });
        const dont_show_in_past = new UICheckbox({ title: 'Don\'t show in "past events"' });
        const dont_show_main_list = new UICheckbox({ title: 'Don\'t show in "main list"' });
        const dont_send_new_session = new UICheckbox({ title: 'Don\'t send "new session" emails' });

        const payout_type_values: UICheckboxRadioOptions<EPayoutType>[] = [
            { key: 'transfer', title: 'Transfer' },
            { key: 'invoice', title: 'Invoicing' },
        ];

        const payout_type = new UIExclusiveToggles(payout_type_values, v => new UIRadio(v), { classes: 'l-toggle-row' });
        const picture_ids = new ABNewPicturesArray('+ Add picture', 'event', false, true);

        const team_emails = new UISelect<string>(undefined, { kind: 'k-subtle', placeholder: $i18n.pr.event_email_team });

        const addOtherEmail = () => {
            const key = other_emails.value;
            if(!key) return;
            other_emails.value = '';
            if(!producer_emails.value.find(s => s.email == key))
                producer_emails.push({ email: key, kinds: ['booking_list'] });
        }

        const other_emails = new UITextInput({ placeholder: $i18n.pr.event_email_external });
        const other_emails_add = new UIButton({ title: $i18n.aa.b_add, classes: 'm-round m-compact ui-tint-subtle', on_click: addOtherEmail });
        other_emails.on_enter = addOtherEmail;

        const producer_emails = new UIInputAdapter((e: CProducerEmail, entry) => {
            const container = $el.DIV('tl-producer-email i-input');
            const email = new UIChip(e.email, 'm-full');
            const kind_values: UICheckboxRadioOptions<CProducerEmailKind>[] = [{ key: 'booking_list', title: 'Booking list' }, { key: 'fully_booked', title: 'Fully booked' }];
            const kinds = new UIMultiToggles(kind_values, v => new UITickCheckbox(v), { classes: 'l-toggle-row' });
            $on(email, 'remove', () => { producer_emails.remove(entry) });
            container.ui_add([email, $el.S(), kinds]);

            return {
                view: container,
                input: $form({ email: $form.noop(), kinds })
            };
        });

        const og_title = new UITextInput({ null_if_empty: true });
        const og_description = new UITextInput({ lines: 3, null_if_empty: true });
        const og_image = new UIImageInput({ scope: 'event' });

        const form = $form({ _: [dont_market, dont_feature, dont_show_in_past, dont_show_main_list, dont_send_new_session], city_id, venue_id, scene_id, producer_id, coproducer_ids: coproducer_ids.adapter, event_tag_ids, is_published, name, show_scene, description, boost, vat, invoice_name, picture_ids: picture_ids.adapter, keywords, producer_emails, payout_type, og_title, og_description, og_image, media_links });

        $on(event_tag_select, 'value', key => {
            if(!key) return;
            event_tag_select.value = undefined;
            const tag = c_event_tags.maybe(key);
            if(!tag) return;
            const value = event_tag_ids.value;
            if(!value.includes(key))
                event_tag_ids.value = [...value, key];
        });

        $on(team_emails, 'value', key => {
            if(!key) return;
            team_emails.value = undefined;
            if(!producer_emails.value.find(s => s.email == key))
                producer_emails.push({ email: key, kinds: ['booking_list'] });
        });

        const team_email_map = new Map<CProducerId, UIValue<string>[]>();

        const setTeamEmailValues = () => {
            const values: UIValue<string>[] = [];

            const id = producer_id.value;
            if(id && team_email_map.has(id)) {
                values.push(...team_email_map.get(id)!);
            }

            for(const id of coproducer_ids.adapter.value) {
                if(id && team_email_map.has(id)) {
                    values.push(...team_email_map.get(id)!);
                }
            }

            team_emails.values = values;
        }

        $on(producer_id, 'value', async id => {
            if(!id) return;
            if(!team_email_map.has(id)) {
                const r = await cProducerGetAutofill(id);
                team_email_map.set(id, r.booking_users.map(t => ({ key: t.email, name: t.name + ' (' + t.email + ')' })));
            }
            setTeamEmailValues();
        });

        $on(coproducer_ids.adapter, 'change', async () => {
            for(const id of coproducer_ids.adapter.value) {
                if(id && !team_email_map.has(id)) {
                    const r = await cProducerGetAutofill(id);
                    team_email_map.set(id!, r.booking_users.map(t => ({ key: t.email, name: t.name + ' (' + t.email + ')' })));
                }
            }
            setTeamEmailValues();
        });

        if(event)
            form.add({ slug });

        const keyword_suggestions = new ABInputSuggestions(keywords);

        let model = event ?? duplicate;

        if(model) {
            $log('app', 'opening event', model.id);
            venue_id.city = model.city_id;
            scene_id.venue = model.venue_id;
            form.value = model;

            show_scene.value = model.flags.show_scene;
            dont_market.value = model.flags.no_market;
            dont_show_in_past.value = model.flags.no_past;
            dont_show_main_list.value = model.flags.no_main_list;
            dont_feature.value = model.flags.no_feature;
            dont_send_new_session.value = model.flags.no_new_session_mail;
        } else {
            vat.value = 6;
            payout_type.value = 'transfer';

            if(imported) {
                name.value = imported.name;
                if(imported.producer_id) {
                    producer_id.value = imported.producer_id;
                    const p = c_producers.maybe(imported.producer_id);
                    if(p) {
                        const defaults = p.event_defaults
                        if(defaults) {
                            if(defaults.city_id && !city_id.value) {
                                city_id.value = defaults.city_id;
                                if(defaults.venue_id) {
                                    venue_id.value = defaults.venue_id;
                                    if(defaults.scene_id)
                                        scene_id.value = defaults.scene_id;
                                }
                            }
                            if(defaults.event_tag_ids)
                                event_tag_ids.value = defaults.event_tag_ids;
                            if(defaults.description)
                                description.value = defaults.description;
                            if(defaults.event_flags?.no_market)
                                dont_market.value = true;
                            if(defaults.event_flags?.no_past)
                                dont_show_in_past.value = true;
                            if(defaults.event_flags?.no_main_list)
                                dont_show_main_list.value = true;
                            if(defaults.picture_ids)
                                picture_ids.adapter.value = defaults.picture_ids;
                            if(defaults.producer_emails)
                                producer_emails.value = defaults.producer_emails;
                            if(defaults.vat)
                                vat.value = defaults.vat;
                        }
                    }
                }

                if(imported.tag_ids?.length)
                    event_tag_ids.value = imported.tag_ids;
                if(imported.keywords)
                    keywords.value = imported.keywords;
                if(imported.media_links)
                    media_links.value = imported.media_links;
                if(imported.description)
                    description.value = imported.description;
                if(imported.venue_id) {
                    const venue_object = c_venues.maybe(imported.venue_id);
                    if(venue_object) {
                        city_id.value = venue_object.city_id;
                        venue_id.value = imported.venue_id;
                        if(imported.scene_id)
                            scene_id.value = imported.scene_id;
                    }
                }

                if(imported.picture_urls) {
                    for(const p of imported.picture_urls) {
                        $log('app', 'importing picture', p);
                        $http.POST<SUploadResponse>('/service/media/v1/upload_url', { url: p, scope: 'event' }).then(r => {
                            picture_ids.adapter.push(r.id);
                        });
                    }
                }
            } else {
                $on(producer_id, 'value', id => {
                    const p = c_producers.maybe(id!);
                    if(p) {
                        const defaults = p.event_defaults
                        if(defaults) {
                            if(defaults.city_id) {
                                city_id.value = defaults.city_id;
                                if(defaults.venue_id) {
                                    venue_id.value = defaults.venue_id;
                                    if(defaults.scene_id)
                                        scene_id.value = defaults.scene_id;
                                }
                            }
                            if(defaults.event_tag_ids)
                                event_tag_ids.value = defaults.event_tag_ids;
                            if(defaults.description)
                                description.value = defaults.description;
                            if(defaults.event_flags?.no_market)
                                dont_market.value = true;
                            if(defaults.event_flags?.no_past)
                                dont_show_in_past.value = true;
                            if(defaults.event_flags?.no_main_list)
                                dont_show_main_list.value = true;
                            if(defaults.media_links)
                                media_links.value = defaults.media_links;
                            if(defaults.picture_ids)
                                picture_ids.adapter.value = defaults.picture_ids;
                            if(defaults.producer_emails)
                                producer_emails.value = defaults.producer_emails;
                            if(defaults.vat)
                                vat.value = defaults.vat;
                        }
                    }
                })
            }
        }

        const buttons = $el.DIV('l-button-row');

        if(event && !event.is_published) {
            buttons.ui_add(new UIButton({
                title: 'Delete event', classes: 'm-round m-hollow', on_click: () => {
                    cEventDelete(event.id).then(remove_callback);
                }
            }));
        }

        let last_description: string;

        const fillSuggestions = () => {
            const value = yGetI18N(description.value);
            if(last_description !== value) {
                last_description = value;
                keyword_suggestions.suggestions = recommendKeywords(value, keywords.value);
            }
        }

        $on(description, 'blur', fillSuggestions);
        fillSuggestions();

        const media_icons = $el.DIV('tl-admin-media-icons', [
            $el.ICON(UIIcon.BRAND_YOUTUBE),
            $el.ICON(UIIcon.BRAND_SPOTIFY),
            $el.ICON(UIIcon.BRAND_SOUNDCLOUD),
            $el.ICON(UIIcon.BRAND_VIMEO)
        ]);

        const section = $el.FORM('ui-section w-form l-form tl-admin-form', [
            new UILabeled(is_published, { before: 'Published', centered: true, end: true }),
            new UILabeled(producer_id, { above: 'Producer', required: true }),
            new UILabeled(name, { above: 'Name of the event', required: true }),
            $el.DIV('l-input-row', [
                new UILabeled(city_id, { above: 'City', required: true }),
                event && new UILabeled(slug, { above: 'Slug', required: true }),
            ]),
            $el.DIV('l-input-row', [
                new UILabeled(venue_id, { above: 'Venue', required: true }),
                new UILabeled(scene_id, { above: 'Scene', required: true }),
            ]),
            show_scene,
            new UILabeled(coproducer_ids, { above: 'Co-producers', required: false }),
            coproducer_ids.add_button,
            new UILabeled($el.DIV('l-form', [
                event_tag_ids,
                event_tag_select
            ]), { above: 'Tags' }),
            new UILabeled(keywords, { above: 'Keywords' }),
            keyword_suggestions,
            new UILabeled(description, { above: 'About the event' }),
            new UILabeled($el.DIV('l-form', [
                $el.DIV('l-input-row', [media_link, media_link_add]),
                new UIList(media_links, { classes: 'tl-media-chips i-input' }),
            ]), { above: $i18n.aa.f_media_links, above_right: media_icons }),
            new UILabeled(boost, { above: 'Popularity boost' }),
            $el.DIV('l-input-row', [
                new UILabeled(vat, { above: 'VAT (%)' }),
                new UILabeled(invoice_name, { above: 'Name in invoice (if different from event name)' }),
            ]),
            new UILabeled(picture_ids, { above: 'Pictures' }),
            picture_ids.add_button,
            new UILabeled($el.DIV('l-form', [
                team_emails,
                $el.DIV('l-input-row', [other_emails, other_emails_add]),
                new UIList(producer_emails, { classes: 'l-form' }),
            ]), { above: $i18n.aa.f_list_recipients }),
            dont_market,
            dont_show_in_past,
            dont_show_main_list,
            dont_feature,
            dont_send_new_session,
            new UILabeled(payout_type, { above: 'Payment method' }),
            $el.H3('heading', 'Share metadata'),
            $el.P('input-hint', 'Will be used instead of the default one if provided'),
            new UILabeled(og_title, { above: 'og:title' }),
            new UILabeled(og_description, { above: 'og:description' }),
            $el.DIV('l-input-row tl-pictures-row', [
                new UILabeled(og_image, { above: 'og:image', below: 'Recommended: 1200x630 ~100kb', classes: 'r-desktop' })
            ]),
            buttons
        ]);

        const button = new UIButton({
            icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => {
                if(!form.validate()) return;

                const value = form.value;
                const flags: YFlags<CEventFlag> = {
                    no_market: dont_market.value || undefined,
                    no_past: dont_show_in_past.value || undefined,
                    no_main_list: dont_show_main_list.value || undefined,
                    no_feature: dont_feature.value || undefined,
                    no_new_session_mail: dont_send_new_session.value || undefined,
                    show_scene: show_scene.value || undefined,
                }
                value.flags = flags;

                if(value.keywords)
                    cEventKeywordsRegister(value.keywords);

                $log('app', 'value', value);

                if(event) {
                    return cEventUpdate(event, value).then(() => {
                        uiToast('Event saved');
                    });
                } else {
                    if(imported)
                        value.imported_id = imported.id;
                    return cEventCreate(value).then(event => {
                        add_callback?.(event);
                    });
                }
            }
        });
        addTooltip(event ? 'Update' : 'Create and add sessions', button, { offset: 8, hover: button, position: UIRelPos.Above });

        this.ui_add([section, sidebar, button]);
    }
}

class SessionCard extends UIView {
    constructor(session: CSession, refresh: () => void) {
        super('div', 'tl-admin-card');

        let booked_text = session.quantity_reserved + ' / ' + session.quantity + ' tickets booked';
        if(session.quantity_standby)
            booked_text += ' | ' + session.quantity_standby + ' standby';

        const edit = new UIButton({
            title: 'EDIT', icon: UIIcon.PEN_TO_SQUARE, on_click: () => {
                $location.push('session', { event_id: session.event_id, session_id: session.id });
            }
        });

        const list = new UIButton({
            title: 'BOOKING LIST', icon: UIIcon.LIST_UL, on_click: () => {
                $location.push('event_bookings', { session_id: session.id });
            }
        });

        const cancel = new UIButton({
            title: 'CANCEL SESSION', icon: UIIcon.XMARK, on_click: async () => {
                if(session.quantity_reserved)
                    await showWarning({ text: "Det är inbokade medlemmar till den här event-sessionen. Är du säker på att du vill ta bort speltiden?" });
                await cSessionCancel(session.id);
                refresh();
            }
        });

        const repeat = new UIButton({
            title: 'REPEAT SESSION', icon: UIIcon.REPEAT, on_click: () => {
                $location.push('repeat_session', { event_id: session.event_id, session_id: session.id });
            }
        });

        this.ui_add([
            $el.B(cSessionFullTime(session)),
            $el.DIV('tl-card-info', booked_text),
            $el.DIV('r-buttons', [edit, list, cancel, repeat])
        ]);
    }
}

class ImportedSessionCard extends UIView {
    session: ISession;
    private _checkbox: UICheckbox;

    constructor(session: ISession) {
        super('div', 'tl-admin-card');

        this.session = session;

        this.ui_add($el.DIV('r-main', [
            this._checkbox = new UICheckbox,
            $el.DIV('l-v', [
                $el.B(yIntervalTime($time(session.time), undefined, tz)),
                // $el.B(session.available+' tickets available', 'tl-card-info')
            ])
        ]));
    }

    get selected() {
        return this._checkbox.value;
    }
}

class VideoCard extends UIView {
    constructor(video: CVideo, event: CEvent, refresh: () => void) {
        super('div', 'tl-admin-card');

        const edit = new UIButton({
            title: 'EDIT', icon: UIIcon.PEN_TO_SQUARE, on_click: () => {
                $location.push({ transient: new VideoScreen(event, video, refresh) });
            }
        });

        const remove = new UIButton({
            title: video.date ? 'CANCEL STREAM' : 'REMOVE VIDEO', icon: UIIcon.TRASH_CAN, on_click: () => {
                if(video.date && video.is_ppv) {
                    return showWarning({ text: 'Cancelling will refund all purchases. Are you sure?' }).then(() => {
                        return cVideoDelete(event.id, video.id).then(refresh, uiToast);
                    });
                } else {
                    return cVideoDelete(event.id, video.id).then(refresh, uiToast);
                }
            }
        });

        const v_video = v_videos.maybe(video.video_id)!;
        this.ui_add($el.B(v_video.name));

        if(video.date)
            this.ui_add($el.DIV('tl-card-info', 'LIVE ' + $time(video.date).formatIn('EEE d MMM yyyy HH:mm', tz)));

        if(video.is_ppv)
            this.ui_add($el.DIV('tl-card-info', 'PPV'));

        //TODO
        // if(video.is_open)
        //     this.ui_add($el.DIV('tl-card-info', 'Open'));
        // else if(video.is_member_open)
        //     this.ui_add($el.DIV('tl-card-info', 'Open for members'));

        this.ui_add(video.is_cancelled ? $el.DIV('tl-card-info', 'Cancelled') : $el.DIV('r-buttons', [edit, remove]));
    }
}

class EventSessionsTab extends UIView {
    constructor(event: CEventAdminView) {
        super('div', 'tl-admin-page');

        const refresh_sessions = () => {
            cEventAdminViewGet(event.id).then(updated_event => {
                Object.assign(event, updated_event);
                const sessions = cEventActiveSessions(event);
                sessions_adapter.data = sessions;
                update_sessions.ui_show(undefined, sessions.filter(s => !cSessionSalesEnded(s)).length > 1);
            });
        };

        const sessions_adapter = new UIDataAdapter((s: CSession) => new SessionCard(s, refresh_sessions));

        const new_session = new UIButton({
            title: '+ Add session', kind: 'k-text', on_click: () => {
                $location.push('session', { event_id: event.id });
            }
        });

        this.root.ui_listen('ui-view-reappeared', refresh_sessions);

        const sessions = new UIList(sessions_adapter, { classes: 'r-grid' });

        const section = $el.FORM('ui-section w-form l-form', [sessions, new_session]);

        cBookingsByEvent(event.id).then(bookings => {
            const emails = bookings.filter(b => b.quantity_booked > 0 && !b.is_test).map(b => b.user_email).join('\n');
            const emails_url = URL.createObjectURL(yTextToBlob(emails, 'text/plain'));
            const emails_filename = event.slug + ' - all emails.txt';
            section.ui_add(new UIButton({ title: 'Download emails for all attendees', kind: 'k-text', link: { href: emails_url, download: emails_filename } }));
        });

        const update_sessions = new UIButton({
            title: 'Update all future sessions', kind: 'k-text', on_click: () => {
                const future_sessions = cEventActiveSessions(event).filter(s => !cSessionSalesEnded(s));
                $location.push({ transient: new UpdateSessionsScreen(event, future_sessions) });
            }
        });
        section.ui_add(update_sessions);

        refresh_sessions();

        if(event.imported_id) {
            const imported_sessions = new UIDataAdapter((s: ISession) => new ImportedSessionCard(s))
            let all_sessions: ISession[];
            i_events.get(event.imported_id).then(e => {
                all_sessions = e.sessions;
                const now = $time.now();
                imported_sessions.data = all_sessions.filter(i_s => $time(i_s.time).after(now) && !cEventActiveSessions(event).some(e_s => $time(i_s.time).equal($time(e_s.time))));
                if(xavis_experiments)
                    imported_sessions.data = all_sessions;
            });

            section.ui_add([
                $el.B('AVAILABLE SESSIONS'),
                new UIList(imported_sessions, { classes: 'r-check-grid' }),
                new UIButton({
                    title: 'Import selected sessions', classes: 'm-round m-compact', on_click: () => {
                        const sessions: ISession[] = [];
                        for(const v of imported_sessions.views) {
                            if(v.selected)
                                sessions.push(v.session);
                        }

                        if(sessions.length) {
                            new ImportSessionsDialog(event, sessions, event.sessions.length ? event.sessions[event.sessions.length - 1] : undefined, c_producers.maybe(event.producer_id)?.event_defaults).present().then(() => {
                                refresh_sessions();
                                if(all_sessions) {
                                    const now = $time.now();
                                    imported_sessions.data = all_sessions.filter(i_s => $time(i_s.time).after(now) && !cEventActiveSessions(event).some(e_s => $time(i_s.time).equal($time(e_s.time))));
                                }
                            });
                        }
                    }
                })
            ]);
        }

        this.ui_add(section);
    }
}

class AddVideoDialog extends UIDialog {
    constructor(event: CEventAdminView) {
        super({ close: true, classes: 'm-500' });

        const vod = new ABVideoSelect({ required: true, placeholder: 'Choose VOD' });
        const date = new UIDateTimeInput({ placeholder: 'Stream date' });
        const expires = new UIDateTimeInput({ placeholder: 'Expiration date (optional)' });
        const is_ppv = new UICheckbox({ title: 'PPV' });
        const is_open = new UICheckbox({ title: 'Open to everyone with the link' });
        const is_member_open = new UICheckbox({ title: 'Open to inactive members' });

        const form = $form({ vod, expires, is_ppv, is_open, is_member_open });

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('title', 'Add video'),
            vod,
            date,
            expires,
            is_ppv,
            is_open,
            is_member_open,
            $el.DIV('l-button-row', new UIButton({
                title: "Apply", classes: 'm-round m-compact', on_click: () => {
                    if(!form.validate()) return;
                    return cVideosCreate(event.id, [form.value]).then(() => {
                        this.dismiss(true);
                    }, uiToast);
                }
            }))
        ]));
    }
}

class EventVideosTab extends UIView {
    constructor(event: CEventAdminView) {
        super('div', 'tl-admin-page');

        v_videos.getAll(true).then(() => {
            const refresh_videos = () => { videos_adapter.data = event.videos || [] };

            const videos_adapter = new UIDataAdapter((v: CVideo) => new VideoCard(v, event, refresh_videos), event.videos);

            const new_video = new UIButton({
                title: '+ Add Video', kind: 'k-text', on_click: () => {
                    new AddVideoDialog(event).present().then(refresh_videos);
                }
            })

            const videos = new UIList(videos_adapter, { classes: 'r-grid' });
            const section = $el.FORM('ui-section w-form l-form', [videos, new_video]);
            this.ui_add(section);
        })

    }
}

class MGCard extends UIView {
    constructor(_event: CEventAdminView, mg: EMinimumGuarantee) {
        super('div', 'tl-admin-card');

        // const edit = new UIButton({ title: 'EDIT', icon: UIIcon.PEN_TO_SQUARE, on_click: () => {
        //     $location.push(new VideoScreen(event, video, refresh));
        // }});

        // const remove = new UIButton({ title: video.stream ? 'CANCEL STREAM' : 'REMOVE VIDEO', icon: UIIcon.TRASH_CAN, on_click: () => {
        //     if(video.stream && video.is_ppv) {
        //         return showWarning({ text: 'Cancelling will refund all purchases. Are you sure?' }).then(() => {
        //             return $tl.removeEventVideo(event.model.id, video.id).then(refresh, message => { toast(message) });
        //         });
        //     } else {
        //         return $tl.removeEventVideo(event.model.id, video.id).then(refresh, message => { toast(message) });
        //     }
        // }});

        // const video = event.getVideo(mg.video);
        // if(video) {
        //     if(video.stream) {
        //         const stream = $tl.streams.maybe(video.stream);
        //         if(stream)
        //             this.ui_add($el.B(stream.name));
        //     }

        //     if(video.vod) {
        //         const vod = $tl.vods.maybe(video.vod);
        //         if(vod)
        //             this.ui_add($el.B(vod.name));
        //     }
        // }

        this.ui_add($el.B($format.currency(mg.amount_total) + ' (' + $format.currency(mg.amount_available) + ' available)'));
        const from = $date(mg.from);
        const to = $date(mg.to);
        this.ui_add($el.DIV('tl-card-info', from.format('d MMM yyyy') + ' - ' + to.format('d MMM yyyy')));
    }
}

class AddMGDialog extends UIDialog<EMinimumGuarantee[]> {
    constructor(event: CEventAdminView) {
        super({ close: true, classes: 'm-500' });

        const video_values: UIValue<YId>[] = [];
        for(const v of event.videos || []) {
            const video = v_videos.maybe(v.video_id)!;
            video_values.push({ key: v.id, name: video.name });
        }
        const video = new UISelect(video_values, { required: true });
        video.value = video_values[0].key;
        const total_amount = new UIFixedCurrencyInput({ default_currency: 'SEK', required: true });
        const from = new UIDateInput({ placeholder: 'From' });
        const to = new UIDateInput({ placeholder: 'To' });

        const mg_form = $form({ video, total_amount, from, to });

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('title', 'Add minimum guarantee'),
            video,
            total_amount,
            from,
            to,
            $el.DIV('l-button-row', new UIButton({
                title: "Apply", classes: 'm-round m-compact', on_click: () => {
                    if(!mg_form.validate()) return;
                    return eMinimumGuaranteeCreate(event.id, mg_form.value).then(mgs => {
                        this.dismiss(mgs);
                    }, uiToast);
                }
            }))
        ]));
    }
}

class EventEconomyTab extends UIView {
    constructor(event: CEventAdminView) {
        super('div', 'tl-admin-page');

        v_videos.getAll(true).then(() => {
            eMinimumGuaranteesByEvent(event.id).then(mgs => {
                const mgs_adapter = new UIDataAdapter((mg: EMinimumGuarantee) => new MGCard(event, mg), mgs);

                const new_mg = new UIButton({
                    title: '+ Add minimum guarantee', kind: 'k-text', on_click: () => {
                        new AddMGDialog(event).present().then(mgs => {
                            mgs_adapter.data = mgs;
                        });
                    }
                });

                const list = new UIList(mgs_adapter, { classes: 'r-grid' });
                const section = $el.FORM('ui-section w-form l-form', [$el.H3('heading', 'Minimum guarantees'), list, new_mg]);
                this.ui_add(section);
            });
        });
    }
}

export class AdminEventScreen extends UIScreen {
    constructor(private _event_id?: YId, private _duplicate?: boolean, private _imported_id?: YId) {
        super();
        this.title = _event_id ? 'Event' : 'New event';
    }

    protected override async fillContent(content: UIViewSpec) {
        content.classList.add('tl-tabs');

        let [event, imported] = await Promise.all([this._event_id && cEventAdminViewGet(this._event_id), this._imported_id && i_events.get(this._imported_id)]);

        if(event) {
            this.title = yGetI18N(event.name);
        }

        let tabs: UITabs;

        let duplicate: CEventAdminView | undefined;
        if(this._duplicate && event) {
            duplicate = cEventDuplicate(event);
            event = undefined;
        }
        let add_callback: ((e: CEventAdminView) => void) | undefined = e => {
            event = e;
            duplicate = undefined;
            add_callback = undefined;
            tabs.setCurrent(1);
        }

        const remove_callback = () => {
            $location.pop();
        }

        content.ui_add(tabs = new UITabs([
            { title: 'Info', view: () => new EventInfoTab(event, duplicate, remove_callback, add_callback, imported) },
            { title: 'Sessions', view: () => new EventSessionsTab(event!) },
            { title: 'Videos', view: () => new EventVideosTab(event!) },
            { title: 'Economy', view: () => new EventEconomyTab(event!) }
        ]));
    }

    override onViewDidReappear(): void {
        this.root.ui_trigger('ui-view-reappeared', undefined);
    }
}
